import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../lib/api';
import { toast } from 'sonner';

// Password strength calculator
const calculatePasswordStrength = (password: string): { score: number; label: string; color: string } => {
  let score = 0;
  
  if (password.length >= 6) score += 1;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
  
  if (score <= 2) return { score, label: 'Zayıf', color: 'bg-red-500' };
  if (score <= 4) return { score, label: 'Orta', color: 'bg-yellow-500' };
  return { score, label: 'Güçlü', color: 'bg-green-500' };
};

const ForceChangePassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const passwordStrength = useMemo(() => calculatePasswordStrength(newPassword), [newPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    if (passwordStrength.score < 3) {
      toast.error('Lütfen daha güçlü bir şifre seçin');
      return;
    }

    setLoading(true);
    try {
      const response = await auth.forceChangePassword(newPassword);
      
      // Update user in localStorage with the server's response
      if (response.data && response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } else {
        // Fallback: update must_change_password manually
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.must_change_password = false;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      toast.success('Şifre başarıyla değiştirildi');
      
      // Small delay to ensure localStorage is updated before navigation
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 100);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#6b0f1a] mb-2">Şifre Değiştirme Zorunlu</h1>
            <p className="text-gray-600">Güvenlik için lütfen şifrenizi değiştirin</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yeni Şifre
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b0f1a] focus:border-transparent pr-12"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${passwordStrength.color} transition-all duration-300`}
                        style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.label === 'Zayıf' ? 'text-red-600' :
                      passwordStrength.label === 'Orta' ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Güçlü şifre için: büyük/küçük harf, rakam ve özel karakter kullanın
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yeni Şifre (Tekrar)
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6b0f1a] focus:border-transparent ${
                  confirmPassword && confirmPassword !== newPassword 
                    ? 'border-red-500' 
                    : 'border-gray-300'
                }`}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
              {confirmPassword && confirmPassword !== newPassword && (
                <p className="text-xs text-red-600 mt-1">Şifreler eşleşmiyor</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || passwordStrength.score < 3 || newPassword !== confirmPassword}
              className="w-full bg-[#6b0f1a] text-white py-3 rounded-lg font-semibold hover:bg-[#8a1422] disabled:opacity-50 transition-colors"
              data-testid="save-password-button"
            >
              {loading ? 'Şifre değiştiriliyor...' : 'Şifreyi Değiştir'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForceChangePassword;
